import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, XCircle, Loader2, Globe, KeyRound, Camera } from "lucide-react";
import { toast } from "sonner";

const STAGE_STYLE = {
  payment: "bg-status-overdue/15 text-status-overdue",
  payment_review: "bg-status-rented/15 text-status-rented",
  handover: "bg-status-available/15 text-status-available",
  active: "bg-status-available/15 text-status-available",
  closed: "bg-muted text-muted-foreground",
};
const STAGE_LABEL = { payment: "Awaiting payment", payment_review: "Payment claimed — verify", handover: "Ready for handover", active: "Active", closed: "Closed" };

function HandoverDialog({ booking, onDone }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photos, setPhotos] = useState(false);
  const [form, setForm] = useState({ odometer_out: "", charge_out: "", battery_serial_out: "", selfie_match: false });
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/operator/bookings/${booking.id}/handover`, {
        photo_sides: photos ? ["front", "back", "left", "right"] : [],
        photo_geo: { lat: 11.95, long: 79.83 },
        selfie_match: form.selfie_match,
        odometer_out: form.odometer_out === "" ? null : Number(form.odometer_out),
        charge_out: form.charge_out === "" ? null : Number(form.charge_out),
        battery_serial_out: form.battery_serial_out,
        payment_cutoff_hours: 24, staff_confirm: true,
      });
      if (data.activated) { toast.success("Bike handed over — ride is active"); setOpen(false); onDone?.(); }
      else toast.error("Blocked: " + (data.blockers || []).join(", "));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`complete-handover-${booking.id}`} size="sm" className="gap-1.5 bg-tenant-primary text-tenant-primary-foreground hover:opacity-90"><CheckCircle2 className="h-3.5 w-3.5" /> Hand over</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Hand over · {booking.vehicle_reg}</DialogTitle><DialogDescription>Quick on-the-spot check before the tourist rides off.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Button data-testid="ho-photos" type="button" variant={photos ? "default" : "outline"} className="w-full gap-2" onClick={() => setPhotos(true)}><Camera className="h-4 w-4" /> {photos ? "4 photos captured ✓" : "Capture all-sides photos"}</Button>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Odometer (km)</Label><Input data-testid="ho-odometer" type="number" value={form.odometer_out} onChange={(e) => setForm({ ...form, odometer_out: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Charge (%)</Label><Input data-testid="ho-charge" type="number" value={form.charge_out} onChange={(e) => setForm({ ...form, charge_out: e.target.value })} /></div>
          </div>
          <div className="space-y-1.5"><Label>Battery serial</Label><Input data-testid="ho-battery" value={form.battery_serial_out} onChange={(e) => setForm({ ...form, battery_serial_out: e.target.value })} /></div>
          <label className="flex items-center gap-2 text-sm"><input data-testid="ho-selfie-match" type="checkbox" checked={form.selfie_match} onChange={(e) => setForm({ ...form, selfie_match: e.target.checked })} /> Selfie matches the tourist</label>
        </div>
        <DialogFooter><Button data-testid="ho-submit" onClick={submit} disabled={saving} className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Activate ride</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TouristBookings() {
  const [rows, setRows] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/operator/tourist-bookings");
    setRows(data);
  }, []);

  useEffect(() => { load(); timer.current = setInterval(load, 6000); return () => clearInterval(timer.current); }, [load]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); await load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const confirm = (id) => act(() => api.post(`/operator/tourist-bookings/${id}/confirm-payment`), "Payment confirmed — ready for handover");
  const reject = (id) => act(() => api.post(`/operator/tourist-bookings/${id}/reject-payment`, { reason: "Payment not received" }), "Payment claim rejected");

  if (!rows) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Tourist bookings</h1>
        <p className="mt-1 text-sm text-muted-foreground">Self-service bookings from your public page. Verify the UPI payment landed in your account, confirm it, then hand over the bike.</p>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-tenant-card border border-dashed border-tenant-border p-12 text-center">
          <Globe className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">No online bookings yet. Share your booking link: <span className="font-mono">/book/&lt;your-subdomain&gt;</span></p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} data-testid={`tourist-bk-row-${r.id}`} className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-4 shadow-tenant-soft">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold">{r.customer_name} · {r.vehicle_reg}</p>
                  <p className="text-sm text-muted-foreground">{r.vehicle_model} · {r.customer_phone} · Ref {r.booking_ref}</p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STAGE_STYLE[r.stage] || "bg-muted"}`}>{STAGE_LABEL[r.stage] || r.stage}</span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span><KeyRound className="mr-1 inline h-3 w-3" />₹{r.amount_due?.toLocaleString()} due (dep ₹{r.deposit_required?.toLocaleString()} + adv ₹{r.advance_amount?.toLocaleString()})</span>
                {r.payment_claim?.upi_reference && <span className="font-medium text-foreground">UPI ref: {r.payment_claim.upi_reference}</span>}
                {r.payment_claim?.claimed_at && !r.payment_claim?.upi_reference && <span className="font-medium text-foreground">marked paid</span>}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {(r.stage === "payment" || r.stage === "payment_review") && (
                  <Button data-testid={`confirm-pay-${r.id}`} size="sm" variant="outline" className="gap-1.5 text-status-available" onClick={() => confirm(r.id)}><CheckCircle2 className="h-3.5 w-3.5" /> Confirm payment</Button>
                )}
                {r.stage === "payment_review" && (
                  <Button data-testid={`reject-pay-${r.id}`} size="sm" variant="outline" className="gap-1.5 text-status-recovery" onClick={() => reject(r.id)}><XCircle className="h-3.5 w-3.5" /> Reject</Button>
                )}
                {r.stage === "handover" && <HandoverDialog booking={r} onDone={load} />}
                {r.stage === "active" && <span className="inline-flex items-center gap-1.5 text-xs font-medium text-status-available"><CheckCircle2 className="h-3.5 w-3.5" /> On the road</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
